import GenericApi from "@/api/genericApi";
import {
  BusinessFileQueryKey,
  CoPQueryKey,
  GenericStatusQueryKey,
  NaturalPersonPLUSQueryKey,
  NaturalPersonQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import { FormError, ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { getUser, getUserToken } from "@/utils/localStorage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import axios, { AxiosError, isAxiosError } from "axios";
import { formatError } from "@/utils/formatError.tsx";
import { Status } from "@/types/Status.tsx";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/store";
import { setActiveTab } from "@/store/slices/componentSlice";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import "react-phone-input-2/lib/style.css";
import { COUNTRIES } from "@/data/data.tsx";
import {
  checkPermissionByKeyAndValue,
  isBitramp,
  validatePdfFile,
} from "@/utils/utils";
import { IndividualDocType } from "@/types/BusinessDoc";
import {
  NaturalPersonNEW,
  NaturalPersonNEWPLUS,
  StatusForms,
} from "@/types/BusinessInfoNEW";
import { Tooltip } from "flowbite-react";
import MuiPhoneNumber from "mui-phone-number";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../UI/Button";

const NaturalPersonForm: React.FC = () => {
  const TITLES = ["Mr", "Mrs", "Ms", "other"];

  const [titleDropdownOpen, setTitleDropdownOpen] = useState(false);

  const handleTitleSelect = (value: string) => {
    handleChange({
      target: { name: "title", value },
    } as React.ChangeEvent<HTMLInputElement>); // ca să păstrezi același handler
    setTitleDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        titleListRef.current &&
        !titleListRef.current.contains(event.target as Node) &&
        titleButtonRef.current &&
        !titleButtonRef.current.contains(event.target as Node)
      ) {
        setTitleDropdownOpen(false);
        setHighlightedTitleIndex(-1);
      }
    };

    if (titleDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [titleDropdownOpen]);

  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false);

  const handleGenderSelect = (value: string) => {
    handleChange({
      target: { name: "gender", value },
    } as React.ChangeEvent<HTMLInputElement>);
    setGenderDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        genderListRef.current &&
        !genderListRef.current.contains(event.target as Node) &&
        genderButtonRef.current &&
        !genderButtonRef.current.contains(event.target as Node)
      ) {
        setGenderDropdownOpen(false);
        setHighlightedGenderIndex(-1);
      }
    };

    if (genderDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [genderDropdownOpen]);

  const [maritalStatusDropdownOpen, setMaritalStatusDropdownOpen] =
    useState(false);

  const handleMaritalStatusSelect = (value: string) => {
    handleChange({
      target: { name: "marital_status", value },
    } as React.ChangeEvent<HTMLInputElement>);
    setMaritalStatusDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        maritalRef.current &&
        !maritalRef.current.contains(event.target as Node) &&
        maritalButtonRef.current &&
        !maritalButtonRef.current.contains(event.target as Node)
      ) {
        setMaritalStatusDropdownOpen(false);
        setHighlightedMaritalIndex(-1);
      }
    };

    if (maritalStatusDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [maritalStatusDropdownOpen]);

  const [documentTypeDropdownOpen, setDocumentTypeDropdownOpen] =
    useState(false);

  const handleDocumentTypeSelect = (value: string) => {
    handleChange({
      target: { name: "document_type", value },
    } as React.ChangeEvent<HTMLInputElement>);
    setDocumentTypeDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        documentTypeRef.current &&
        !documentTypeRef.current.contains(event.target as Node) &&
        documentTypeButtonRef.current &&
        !documentTypeButtonRef.current.contains(event.target as Node)
      ) {
        setDocumentTypeDropdownOpen(false);
        setHighlightedDocumentTypeIndex(-1);
      }
    };

    if (documentTypeDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [documentTypeDropdownOpen]);

  const [documentCountryDropdownOpen, setDocumentCountryDropdownOpen] =
    useState(false);

  const handleDocumentCountrySelect = (value: string) => {
    handleChange({
      target: { name: "document_country", value },
    } as React.ChangeEvent<HTMLInputElement>);
    setDocumentCountryDropdownOpen(false);
  };

  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);

  const handleCountrySelect = (value: string) => {
    handleChange({
      target: { name: "country", value },
    } as React.ChangeEvent<HTMLInputElement>);
    setCountryDropdownOpen(false);
  };

  const [searchTermDocumentCountry, setSearchTermDocumentCountry] =
    useState("");
  const [highlightedIndexDocumentCountry, setHighlightedIndexDocumentCountry] =
    useState(-1);
  const listRefDocumentCountry = useRef<HTMLDivElement | null>(null);
  const containerRefDocumentCountry = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRefDocumentCountry.current &&
        !containerRefDocumentCountry.current.contains(event.target as Node)
      ) {
        setDocumentCountryDropdownOpen(false);
        setHighlightedIndexDocumentCountry(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      highlightedIndexDocumentCountry >= 0 &&
      documentCountryDropdownOpen &&
      listRefDocumentCountry.current
    ) {
      const offset = 1;
      const el = listRefDocumentCountry.current.children[
        highlightedIndexDocumentCountry + offset
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndexDocumentCountry, documentCountryDropdownOpen]);

  const filtered = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(searchTermDocumentCountry.toLowerCase()),
  );

  const closeDropdown = () => {
    setDocumentCountryDropdownOpen(false);
    setSearchTermDocumentCountry("");
    setHighlightedIndexDocumentCountry(-1);
  };

  const [searchTermCountry, setSearchTermCountry] = useState("");
  const [highlightedIndexCountry, setHighlightedIndexCountry] = useState(-1);
  const listRefCountry = useRef<HTMLDivElement>(null);
  const containerRefCountry = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRefCountry.current &&
        !containerRefCountry.current.contains(event.target as Node)
      ) {
        setCountryDropdownOpen(false);
        setHighlightedIndexCountry(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      highlightedIndexCountry >= 0 &&
      countryDropdownOpen &&
      listRefCountry.current
    ) {
      const offset = 1;
      const el = listRefCountry.current.children[
        highlightedIndexCountry + offset
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndexCountry, countryDropdownOpen]);

  const closeCountryDropdown = () => {
    setCountryDropdownOpen(false);
    setSearchTermCountry("");
    setHighlightedIndexCountry(-1);
  };

  const [highlightedTitleIndex, setHighlightedTitleIndex] = useState(-1);
  const titleListRef = useRef<HTMLDivElement>(null);
  const titleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      highlightedTitleIndex >= 0 &&
      titleDropdownOpen &&
      titleListRef.current
    ) {
      const el = titleListRef.current.children[
        highlightedTitleIndex
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedTitleIndex, titleDropdownOpen]);

  const GENDERS = ["Male", "Female"];
  const [highlightedGenderIndex, setHighlightedGenderIndex] = useState(-1);
  const genderListRef = useRef<HTMLDivElement>(null);
  const genderButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      highlightedGenderIndex >= 0 &&
      genderDropdownOpen &&
      genderListRef.current
    ) {
      const el = genderListRef.current.children[
        highlightedGenderIndex
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedGenderIndex, genderDropdownOpen]);

  const MARITAL_OPTIONS = ["Single", "Married"];
  const [highlightedMaritalIndex, setHighlightedMaritalIndex] = useState(-1);
  const maritalRef = useRef<HTMLDivElement>(null);
  const maritalButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      highlightedMaritalIndex >= 0 &&
      maritalStatusDropdownOpen &&
      maritalRef.current
    ) {
      const el = maritalRef.current.children[
        highlightedMaritalIndex
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedMaritalIndex, maritalStatusDropdownOpen]);

  const DOCUMENT_TYPES = ["Passport", "ID Card"];
  const [highlightedDocumentTypeIndex, setHighlightedDocumentTypeIndex] =
    useState(-1);
  const documentTypeRef = useRef<HTMLDivElement>(null);
  const documentTypeButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      highlightedDocumentTypeIndex >= 0 &&
      documentTypeDropdownOpen &&
      documentTypeRef.current
    ) {
      const el = documentTypeRef.current.children[
        highlightedDocumentTypeIndex
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedDocumentTypeIndex, documentTypeDropdownOpen]);

  const baseUrl: string = GenericApi.baseUrl;

  const today = new Date().toISOString().split("T")[0];

  const { selectedClient } = useSelector((state: RootState) => state.client);
  const user = getUser();

  const getFileName = (filePath: File | string | null) => {
    if (!filePath) return "";
    if (filePath instanceof File) {
      return filePath.name;
    }
    return filePath.split("/").pop();
  };

  const calculateMinimumDateToBe18 = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split("T")[0];
  };

  const minimumDateToBe18 = calculateMinimumDateToBe18();

  const handleOpenDocument = (link: string) => {
    window.open(link, "_blank");
  };

  const [poa, setPOA] = useState<File | null>(null);
  const [poi, setPOI] = useState<File | null>(null);
  const [passid, setPASSID] = useState<File | null>(null);

  const canEdit = user?.is_superuser;

  window.addEventListener("load", () => {
    window.scrollTo({ top: 0, behavior: "auto" });
  });

  const handlePOA = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValidated = validatePdfFile(
      e.target.files ? e.target.files[0] : null,
    );
    if (!isValidated) {
      return;
    }
    setPOA(e.target.files ? e.target.files[0] : null);
  };
  const handlePOI = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValidated = validatePdfFile(
      e.target.files ? e.target.files[0] : null,
    );
    if (!isValidated) {
      return;
    }
    setPOI(e.target.files ? e.target.files[0] : null);
  };
  const handlePASSID = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValidated = validatePdfFile(
      e.target.files ? e.target.files[0] : null,
    );
    if (!isValidated) {
      return;
    }
    setPASSID(e.target.files ? e.target.files[0] : null);
  };

  const { data: userIndividual } = useQuery<ApiResponse<NaturalPersonNEW>>({
    queryFn: async () =>
      GenericApi.get<NaturalPersonNEW>(
        `${
          selectedClient
            ? `${ApiRoutes.INDIVIDUALINFO}?user_id=${selectedClient.id}`
            : `${ApiRoutes.INDIVIDUALINFO}`
        }`,
      ),
    queryKey: [NaturalPersonQueryKey],
  });

  const { data: userIndividualPLUS } = useQuery<
    ApiResponse<NaturalPersonNEWPLUS>
  >({
    queryFn: async () =>
      GenericApi.get<NaturalPersonNEWPLUS>(
        `${
          selectedClient
            ? `${ApiRoutes.INDIVIDUALINFOPLUS}?user_id=${selectedClient.id}`
            : `${ApiRoutes.INDIVIDUALINFOPLUS}`
        }`,
      ),
    queryKey: [NaturalPersonPLUSQueryKey],
  });

  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const { showSnackBar } = useSnackBarAlert();

  const [declineReason, setDeclineReason] = useState<string>("");

  const [formData, setFormData] = useState<NaturalPersonNEW>({
    phone: userIndividual?.data?.phone ?? "",
    title: userIndividual?.data?.title ?? "Mr",
    other_title: userIndividual?.data?.other_title ?? "",
    first_name: userIndividual?.data?.first_name ?? "",
    last_name: userIndividual?.data?.last_name ?? "",
    gender: userIndividual?.data?.gender ?? "Male",
    marital_status: userIndividual?.data?.marital_status ?? "Single",
    email: userIndividual?.data?.email ?? "",
    document_type: userIndividual?.data?.document_type ?? "Passport",
    document_number: userIndividual?.data?.document_number ?? "",
    document_country: userIndividual?.data?.document_country ?? "Gibraltar",
    document_expiration: userIndividual?.data?.document_expiration ?? null,
    street_address: userIndividual?.data?.street_address ?? "",
    street_address2: userIndividual?.data?.street_address2 ?? "",
    post_code: userIndividual?.data?.post_code ?? "",
    city: userIndividual?.data?.city ?? "",
    country: userIndividual?.data?.country ?? "Gibraltar",
    status: {
      status: userIndividual?.data?.status?.status ?? "",
      decline_reason: userIndividual?.data?.status?.decline_reason ?? "",
    },
    date_address_became_residential:
      userIndividual?.data?.date_address_became_residential ?? null,
  });

  const [formDataPlus, setFormDataPlus] = useState<NaturalPersonNEWPLUS>({
    business_name: userIndividualPLUS?.data?.business_name ?? "",
    tax_number: userIndividualPLUS?.data?.tax_number ?? "",
    business_address: userIndividualPLUS?.data?.business_address ?? "",
    office_phone: userIndividualPLUS?.data?.office_phone ?? "",
    business_phone: userIndividualPLUS?.data?.business_phone ?? "",
    office_email: userIndividualPLUS?.data?.office_email ?? "",
    business_email: userIndividualPLUS?.data?.business_email ?? "",
    occupation: userIndividualPLUS?.data?.occupation ?? "",
    mailing_address: userIndividualPLUS?.data?.mailing_address ?? "",
    date_of_birth: userIndividualPLUS?.data?.date_of_birth ?? null,
    town_and_country_of_birth:
      userIndividualPLUS?.data?.town_and_country_of_birth ?? "",
    domicile_country: userIndividualPLUS?.data?.domicile_country ?? "",
    tax_residence_country1:
      userIndividualPLUS?.data?.tax_residence_country1 ?? "",
    tax_residence_number1:
      userIndividualPLUS?.data?.tax_residence_number1 ?? "",
    tax_residence_country2:
      userIndividualPLUS?.data?.tax_residence_country2 ?? "",
    tax_residence_number2:
      userIndividualPLUS?.data?.tax_residence_number2 ?? "",
    tax_residence_country3:
      userIndividualPLUS?.data?.tax_residence_country3 ?? "",
    tax_residence_number3:
      userIndividualPLUS?.data?.tax_residence_number3 ?? "",
  });

  const localUser = getUser();

  useEffect(() => {
    if (userIndividual?.data) {
      setFormData((prev) => ({
        ...prev,
        phone: userIndividual?.data?.phone ?? "",
        title: userIndividual?.data?.title ?? "Mr",
        other_title: userIndividual?.data?.other_title ?? "",
        first_name: userIndividual?.data?.first_name ?? "",
        last_name: userIndividual?.data?.last_name ?? "",
        gender: userIndividual?.data?.gender ?? "Male",
        marital_status: userIndividual?.data?.marital_status ?? "Single",
        email: userIndividual?.data?.email ?? "",
        document_type: userIndividual?.data?.document_type ?? "Passport",
        document_number: userIndividual?.data?.document_number ?? "",
        document_country: userIndividual?.data?.document_country ?? "Gibraltar",
        document_expiration: userIndividual?.data?.document_expiration ?? null,
        date_address_became_residential:
          userIndividual?.data?.date_address_became_residential ?? null,
        street_address: userIndividual?.data?.street_address ?? "",
        street_address2: userIndividual?.data?.street_address2 ?? "",
        post_code: userIndividual?.data?.post_code ?? "",
        city: userIndividual?.data?.city ?? "",
        country: userIndividual?.data?.country ?? "Gibraltar",
        date: new Date().toISOString(),
      }));
      setDeclineReason(userIndividual?.data?.status?.decline_reason ?? "");
    }
    if (userIndividualPLUS?.data) {
      setFormDataPlus((prev) => ({
        ...prev,
        business_name: userIndividualPLUS?.data?.business_name ?? "",
        tax_number: userIndividualPLUS?.data?.tax_number ?? "",
        business_address: userIndividualPLUS?.data?.business_address ?? "",
        office_phone: userIndividualPLUS?.data?.office_phone ?? "",
        business_phone: userIndividualPLUS?.data?.business_phone ?? "",
        office_email: userIndividualPLUS?.data?.office_email ?? "",
        business_email: userIndividualPLUS?.data?.business_email ?? "",
        occupation: userIndividualPLUS?.data?.occupation ?? "",
        mailing_address: userIndividualPLUS?.data?.mailing_address ?? "",
        date_of_birth: userIndividualPLUS?.data?.date_of_birth ?? "",
        town_and_country_of_birth:
          userIndividualPLUS?.data?.town_and_country_of_birth ?? "",
        domicile_country: userIndividualPLUS?.data?.domicile_country ?? "",
        tax_residence_country1:
          userIndividualPLUS?.data?.tax_residence_country1 ?? "",
        tax_residence_number1:
          userIndividualPLUS?.data?.tax_residence_number1 ?? "",
        tax_residence_country2:
          userIndividualPLUS?.data?.tax_residence_country2 ?? "",
        tax_residence_number2:
          userIndividualPLUS?.data?.tax_residence_number2 ?? "",
        tax_residence_country3:
          userIndividualPLUS?.data?.tax_residence_country3 ?? "",
        tax_residence_number3:
          userIndividualPLUS?.data?.tax_residence_number3 ?? "",
      }));
    }
  }, [userIndividual, userIndividualPLUS]);

  const addNaturalPersonFile = useMutation<
    ApiResponse<IndividualDocType>,
    ApiError<ValidationErrors>,
    {
      proof_of_address: File | null;
      proof_of_income: File | null;
      passport_file: File | null;
    }
  >({
    mutationFn: async ({
      passport_file,
      proof_of_address,
      proof_of_income,
    }): Promise<ApiResponse<IndividualDocType>> => {
      const formData = new FormData();

      //TODO Fix this
      formData.append("passport_file", passport_file!);
      formData.append("proof_of_address", proof_of_address!);
      formData.append("proof_of_income", proof_of_income!);

      const apiResponse: ApiResponse<IndividualDocType> = {
        data: null,
        error: null,
      };

      const url = `${
        selectedClient
          ? `${ApiRoutes.INDIVIDUALINFOFILES}?user_id=${selectedClient.id}`
          : ApiRoutes.INDIVIDUALINFOFILES
      }`;

      const res = await axios.post<ApiResponse<IndividualDocType>>(
        `${baseUrl}${url}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        },
      );

      if (res.status !== 200) {
        apiResponse.error = {
          name: "Error",
          message: "An error occurred",
          stack: "",
        } as Error;
        return apiResponse;
      }

      apiResponse.data = {
        passport_file: null,
        proof_of_address: null,
        proof_of_income: null,
      };
      return apiResponse;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [BusinessFileQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const editNaturalPersonMutation = useMutation<
    ApiResponse<NaturalPersonNEW>,
    ApiError<ValidationErrors>,
    NaturalPersonNEW
  >({
    mutationFn: (data) =>
      GenericApi.put<NaturalPersonNEW>(
        `${ApiRoutes.INDIVIDUALINFO}?user_id=${
          selectedClient ? selectedClient.id : ""
        }`,
        {
          ...data,
        },
      ),
    onSuccess: ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [NaturalPersonQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      showSnackBar(
        "Natural Person Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );

      addNaturalPersonFile.mutate({
        proof_of_address: poa,
        proof_of_income: poi,
        passport_file: passid,
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const addNaturalPerson = useMutation<
    ApiResponse<NaturalPersonNEW>,
    ApiError<ValidationErrors>,
    NaturalPersonNEW
  >({
    mutationFn: (data) =>
      GenericApi.put<NaturalPersonNEW>(`${ApiRoutes.INDIVIDUALINFO}`, {
        ...data,
      }),
    onSuccess: ({ data }) => {
      CustomLogger.log(data);
      addNaturalPersonFile.mutate({
        proof_of_address: poa,
        proof_of_income: poi,
        passport_file: passid,
      });

      queryClient.invalidateQueries({
        queryKey: [NaturalPersonQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      dispatch(setActiveTab(2));
      showSnackBar(
        "Natural Person Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const addNaturalPersonPLUS = useMutation<
    ApiResponse<NaturalPersonNEWPLUS>,
    ApiError<ValidationErrors>,
    NaturalPersonNEWPLUS
  >({
    mutationFn: (data) =>
      GenericApi.put<NaturalPersonNEWPLUS>(`${ApiRoutes.INDIVIDUALINFOPLUS}`, {
        ...data,
      }),
    onSuccess: ({ data }) => {
      CustomLogger.log(data);

      queryClient.invalidateQueries({
        queryKey: [NaturalPersonPLUSQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      showSnackBar(
        "Solo entrepreneur Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const editNaturalPersonPLUS = useMutation<
    ApiResponse<NaturalPersonNEWPLUS>,
    ApiError<ValidationErrors>,
    NaturalPersonNEWPLUS
  >({
    mutationFn: (data) =>
      GenericApi.put<NaturalPersonNEWPLUS>(
        `${ApiRoutes.INDIVIDUALINFOPLUS}?user_id=${
          selectedClient ? selectedClient.id : ""
        }`,
        {
          ...data,
        },
      ),
    onSuccess: ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [NaturalPersonPLUSQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      showSnackBar(
        "Solo entrepreneur Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const addNaturalPersonStatus = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    StatusForms
  >({
    mutationFn: (data) => {
      return GenericApi.put<StatusForms>(
        `${ApiRoutes.INDIVIDUALINFOSTATUS}?user_id=${selectedClient?.id}`,
        {
          ...data,
        },
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [NaturalPersonQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      showSnackBar(
        "Natural Person Status Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const [error, setError] = useState<{
    type: "manual";
    message: string;
  }>({
    type: "manual",
    message: "",
  });

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    const defaultErrorMessage = "An error occurred";

    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message: error.response?.data?.detail ?? defaultErrorMessage,
      });
      return;
    }

    if (isAxiosError<ValidationErrors>(error)) {
      setError({
        type: "manual",
        message: formatError(error, defaultErrorMessage),
      });
      return;
    }

    //* Set error message from server
    setError({
      type: "manual",
      message: error?.message ?? defaultErrorMessage,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleChangePLUS = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormDataPlus((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      phone: formData.phone,
      title: formData.title,
      other_title: formData.other_title,
      first_name: formData.first_name,
      last_name: formData.last_name,
      gender: formData.gender,
      marital_status: formData.marital_status,
      email: formData.email,
      document_type: formData.document_type,
      document_number: formData.document_number,
      document_country: formData.document_country,
      document_expiration: formData.document_expiration,
      street_address: formData.street_address,
      street_address2: formData.street_address2,
      post_code: formData.post_code,
      city: formData.city,
      country: formData.country,
      date_address_became_residential: formData.date_address_became_residential,
    };
    const payloadPLUS = {
      business_name: formDataPlus.business_name,
      tax_number: formDataPlus.tax_number,
      business_address: formDataPlus.business_address,
      office_phone: formDataPlus.office_phone,
      business_phone: formDataPlus.business_phone,
      office_email: formDataPlus.office_email,
      business_email: formDataPlus.business_email,
      occupation: formDataPlus.occupation,
      mailing_address: formDataPlus.mailing_address,
      date_of_birth:
        formDataPlus.date_of_birth != "" ? formDataPlus.date_of_birth : null,
      town_and_country_of_birth: formDataPlus.town_and_country_of_birth ?? "",
      domicile_country: formDataPlus.domicile_country ?? "",
      tax_residence_country1: formDataPlus.tax_residence_country1 ?? "",
      tax_residence_number1: formDataPlus.tax_residence_number1 ?? "",
      tax_residence_country2: formDataPlus.tax_residence_country2 ?? "",
      tax_residence_number2: formDataPlus.tax_residence_number2 ?? "",
      tax_residence_country3: formDataPlus.tax_residence_country3 ?? "",
      tax_residence_number3: formDataPlus.tax_residence_number3 ?? "",
    };
    if (
      (poa === null || poi === null || passid === null) &&
      (userIndividual?.data?.proof_of_address === null ||
        userIndividual?.data?.proof_of_income === null ||
        userIndividual?.data?.passport_file === null)
    ) {
      showSnackBar(
        "Please upload all required documents",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      return;
    }
    addNaturalPerson.mutate(payload);
    addNaturalPersonPLUS.mutate(payloadPLUS);
  };

  useEffect(() => {
    if (selectedClient) {
      const statusFromBackend = userIndividual?.data?.status;
      if (statusFromBackend) {
        setFormData((prev: any) => ({
          ...prev,
          status: statusFromBackend,
        }));
      }
    }
  }, [selectedClient, userIndividual]);

  const handleSubmitWithStatus = (
    e: React.FormEvent,
    statusChanged: string,
  ) => {
    e.preventDefault();

    if (selectedClient) {
      const payload = {
        status: statusChanged,
        decline_reason: declineReason,
      };
      setError({
        type: "manual",
        message: "",
      });
      addNaturalPersonStatus.mutate(payload, {
        onSuccess: () => {
          setFormData((prev: any) => ({
            ...prev,
            status: { ...prev.status, status: statusChanged },
          }));
        },
      });
    }
  };

  const canEditNaturalPerson =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "individualinfo",
    ) || user?.is_superuser;

  const isDisabled = !(canEditNaturalPerson || !user?.is_staff);

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="w-full mx-auto p-8 Container pshadow Text rounded-b-[0rem]"
      >
        <div className="flex items-center pt-2 pb-2 w-full">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Individual
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Title <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="flex items-center">
              {isBitramp() ? (
                <div className="relative w-full">
                  <div
                    id="title"
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      if (!isDisabled) {
                        setTitleDropdownOpen(!titleDropdownOpen);
                        setHighlightedTitleIndex(0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (isDisabled) return;

                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (titleDropdownOpen && highlightedTitleIndex >= 0) {
                          handleTitleSelect(TITLES[highlightedTitleIndex]);
                          setTitleDropdownOpen(false);
                          setHighlightedTitleIndex(-1);
                        } else {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(0);
                        }
                      }

                      if (e.key === "Escape") {
                        e.preventDefault();
                        setTitleDropdownOpen(false);
                        setHighlightedTitleIndex(-1);
                      }

                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!titleDropdownOpen) {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(0);
                        } else {
                          setHighlightedTitleIndex((prev) =>
                            prev < TITLES.length - 1 ? prev + 1 : 0,
                          );
                        }
                      }

                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!titleDropdownOpen) {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(TITLES.length - 1);
                        } else {
                          setHighlightedTitleIndex((prev) =>
                            prev > 0 ? prev - 1 : TITLES.length - 1,
                          );
                        }
                      }
                    }}
                    className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] flex items-center justify-between gap-2 SmallText ${
                      isDisabled
                        ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                        : isBitramp()
                          ? "cursor-pointer bg-[#464646] border border-[#2B2B2B] text-white"
                          : "cursor-pointer bg-white border border-[#D4D4D4]"
                    }`}
                  >
                    {formData.title || "Select Title"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="10"
                      viewBox="0 0 11 10"
                      fill="none"
                    >
                      <path
                        d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                        fill={isBitramp() ? "#fff" : "#737791"}
                      />
                    </svg>
                  </div>

                  <AnimatePresence>
                    {titleDropdownOpen && !isDisabled && (
                      <motion.div
                        ref={titleListRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute top-full left-0 w-full mt-1 z-10 max-h-60 overflow-y-auto rounded-[0.625rem] shadow-md ${
                          isBitramp()
                            ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                            : "bg-white border border-gray-200"
                        }`}
                      >
                        {TITLES.map((title, index) => (
                          <div
                            key={title}
                            onClick={() => {
                              handleTitleSelect(title);
                              setTitleDropdownOpen(false);
                              setHighlightedTitleIndex(-1);
                            }}
                            onMouseEnter={() => setHighlightedTitleIndex(index)}
                            className={`px-4 py-2 cursor-pointer ${
                              isBitramp()
                                ? highlightedTitleIndex === index
                                  ? "bg-[#262626]"
                                  : "hover:bg-[#262626]"
                                : highlightedTitleIndex === index
                                  ? "bg-gray-100"
                                  : "hover:bg-gray-100"
                            }`}
                          >
                            {title}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="relative w-full">
                  <div
                    id="title"
                    tabIndex={0}
                    role="button"
                    ref={titleButtonRef}
                    onClick={() => {
                      if (!isDisabled) {
                        setTitleDropdownOpen(!titleDropdownOpen);
                        setHighlightedTitleIndex(0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (isDisabled) return;

                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (titleDropdownOpen && highlightedTitleIndex >= 0) {
                          handleTitleSelect(TITLES[highlightedTitleIndex]);
                          setTitleDropdownOpen(false);
                          setHighlightedTitleIndex(-1);
                        } else {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(0);
                        }
                      }

                      if (e.key === "Escape") {
                        e.preventDefault();
                        setTitleDropdownOpen(false);
                        setHighlightedTitleIndex(-1);
                      }

                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!titleDropdownOpen) {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(0);
                        } else {
                          setHighlightedTitleIndex((prev) =>
                            prev < TITLES.length - 1 ? prev + 1 : 0,
                          );
                        }
                      }

                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!titleDropdownOpen) {
                          setTitleDropdownOpen(true);
                          setHighlightedTitleIndex(TITLES.length - 1);
                        } else {
                          setHighlightedTitleIndex((prev) =>
                            prev > 0 ? prev - 1 : TITLES.length - 1,
                          );
                        }
                      }
                    }}
                    className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] flex items-center justify-between gap-2 SmallText ${
                      isDisabled
                        ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                        : isBitramp()
                          ? "cursor-pointer bg-[#464646] border border-[#2B2B2B] text-white"
                          : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                    }`}
                  >
                    {formData.title || "Select Title"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="10"
                      viewBox="0 0 11 10"
                      fill="none"
                    >
                      <path
                        d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                        fill={isBitramp() ? "#fff" : "#737791"}
                      />
                    </svg>
                  </div>

                  <AnimatePresence>
                    {titleDropdownOpen && !isDisabled && (
                      <motion.div
                        ref={titleListRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute top-full left-0 w-full mt-1 z-10 max-h-60 overflow-y-auto rounded-[0.625rem] shadow-md ${
                          isBitramp()
                            ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                            : "bg-white border border-gray-200"
                        }`}
                      >
                        {TITLES.map((title, index) => (
                          <div
                            key={title}
                            onClick={() => {
                              handleTitleSelect(title);
                              setTitleDropdownOpen(false);
                              setHighlightedTitleIndex(-1);
                            }}
                            onMouseEnter={() => setHighlightedTitleIndex(index)}
                            className={`px-4 py-2 cursor-pointer ${
                              isBitramp()
                                ? highlightedTitleIndex === index
                                  ? "bg-[#262626]"
                                  : "hover:bg-[#262626]"
                                : highlightedTitleIndex === index
                                  ? "bg-gray-100"
                                  : "hover:bg-gray-100"
                            }`}
                          >
                            {title}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <input
                type="text"
                name="other_title"
                placeholder="Other title"
                value={formData.other_title}
                onChange={handleChange}
                className={`input_design Input ms-2 ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={formData.title !== "other" || isDisabled}
              />
            </div>
          </div>
          <div className="basis-full" />
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              First Name <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="First Name"
              required
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Last Name <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="Last Name"
              required
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Gender <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="relative w-full">
              <div
                id="gender"
                tabIndex={0}
                role="button"
                ref={genderButtonRef}
                onClick={() => {
                  if (!isDisabled) {
                    setGenderDropdownOpen(!genderDropdownOpen);
                    setHighlightedGenderIndex(0);
                  }
                }}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (genderDropdownOpen && highlightedGenderIndex >= 0) {
                      handleGenderSelect(GENDERS[highlightedGenderIndex]);
                      setGenderDropdownOpen(false);
                      setHighlightedGenderIndex(-1);
                    } else {
                      setGenderDropdownOpen(true);
                      setHighlightedGenderIndex(0);
                    }
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    setGenderDropdownOpen(false);
                    setHighlightedGenderIndex(-1);
                  }

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (!genderDropdownOpen) {
                      setGenderDropdownOpen(true);
                      setHighlightedGenderIndex(0);
                    } else {
                      setHighlightedGenderIndex((prev) =>
                        prev < GENDERS.length - 1 ? prev + 1 : 0,
                      );
                    }
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    if (!genderDropdownOpen) {
                      setGenderDropdownOpen(true);
                      setHighlightedGenderIndex(GENDERS.length - 1);
                    } else {
                      setHighlightedGenderIndex((prev) =>
                        prev > 0 ? prev - 1 : GENDERS.length - 1,
                      );
                    }
                  }
                }}
                className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                    : isBitramp()
                      ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                      : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                }`}
              >
                {formData.gender || "Select Gender"}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                >
                  <path
                    d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                    fill={isBitramp() ? "#fff" : "#737791"}
                  />
                </svg>
              </div>

              <AnimatePresence>
                {genderDropdownOpen && !isDisabled && (
                  <motion.div
                    ref={genderListRef}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`absolute top-full left-0 w-full mt-1 z-10 max-h-60 overflow-y-auto rounded-[0.625rem] shadow-md ${
                      isBitramp()
                        ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    {GENDERS.map((gender, index) => (
                      <div
                        key={gender}
                        onClick={() => {
                          handleGenderSelect(gender);
                          setGenderDropdownOpen(false);
                          setHighlightedGenderIndex(-1);
                        }}
                        onMouseEnter={() => setHighlightedGenderIndex(index)}
                        className={`px-4 py-2 cursor-pointer ${
                          isBitramp()
                            ? highlightedGenderIndex === index
                              ? "bg-[#262626]"
                              : "hover:bg-[#262626]"
                            : highlightedGenderIndex === index
                              ? "bg-gray-100"
                              : "hover:bg-gray-100"
                        }`}
                      >
                        {gender}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Marital Status <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="relative w-full">
              <div
                id="marital_status"
                role="button"
                tabIndex={0}
                ref={maritalButtonRef}
                onClick={() => setMaritalStatusDropdownOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      maritalStatusDropdownOpen &&
                      highlightedMaritalIndex >= 0
                    ) {
                      handleMaritalStatusSelect(
                        MARITAL_OPTIONS[highlightedMaritalIndex],
                      );
                      setMaritalStatusDropdownOpen(false);
                      setHighlightedMaritalIndex(-1);
                    } else {
                      setMaritalStatusDropdownOpen(true);
                      setHighlightedMaritalIndex(0);
                    }
                  }

                  if (e.key === "Escape") {
                    setMaritalStatusDropdownOpen(false);
                    setHighlightedMaritalIndex(-1);
                  }

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setMaritalStatusDropdownOpen(true);
                    setHighlightedMaritalIndex((prev) =>
                      prev < MARITAL_OPTIONS.length - 1 ? prev + 1 : 0,
                    );
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setMaritalStatusDropdownOpen(true);
                    setHighlightedMaritalIndex((prev) =>
                      prev > 0 ? prev - 1 : MARITAL_OPTIONS.length - 1,
                    );
                  }
                }}
                className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                    : isBitramp()
                      ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                      : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                }`}
              >
                {formData.marital_status || "Select Marital Status"}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                >
                  <path
                    d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                    fill={isBitramp() ? "#fff" : "#737791"}
                  />
                </svg>
              </div>

              <AnimatePresence>
                {maritalStatusDropdownOpen && !isDisabled && (
                  <motion.div
                    ref={maritalRef}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`${
                      isBitramp()
                        ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                        : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                    }`}
                  >
                    {MARITAL_OPTIONS.map((option, index) => (
                      <div
                        key={option}
                        onClick={() => {
                          handleMaritalStatusSelect(option);
                          setMaritalStatusDropdownOpen(false);
                          setHighlightedMaritalIndex(-1);
                        }}
                        onMouseEnter={() => setHighlightedMaritalIndex(index)}
                        className={`px-4 py-2 cursor-pointer ${
                          isBitramp()
                            ? highlightedMaritalIndex === index
                              ? "bg-[#262626]"
                              : "hover:bg-[#262626]"
                            : highlightedMaritalIndex === index
                              ? "bg-gray-100"
                              : "hover:bg-gray-100"
                        }`}
                      >
                        {option}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              E-mail <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email != "" ? formData.email : user?.email}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="E-mail"
              required
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5 flex flex-col justify-between">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Phone <span className="!text-red-500 ml-1">*</span>
            </label>

            <MuiPhoneNumber
              defaultCountry="gi"
              value={formData.phone}
              disabled={isDisabled}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  phone: typeof value === "string" ? value : "",
                })
              }
              inputClass={`input_design Input w-full ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  ":hover": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  "& input": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                "& .MuiInputAdornment-root": {
                  marginLeft: "0.6rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiPaper-root": {
                  backgroundColor: "#2B2B2B !important",
                  color: "#fff !important",
                },
                ".MuiMenuItem-root": {
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#1a1a1a !important",
                  },
                },
                ".Mui-selected": {
                  backgroundColor: "#202a43 !important",
                },
              }}
            />
          </div>
        </div>

        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Series and number of the identity document
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646]rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem] ">
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Type of Document <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="relative w-full">
              <div
                id="document_type"
                role="button"
                tabIndex={0}
                ref={documentTypeButtonRef}
                onClick={() => setDocumentTypeDropdownOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      documentTypeDropdownOpen &&
                      highlightedDocumentTypeIndex >= 0
                    ) {
                      handleDocumentTypeSelect(
                        DOCUMENT_TYPES[highlightedDocumentTypeIndex],
                      );
                      setDocumentTypeDropdownOpen(false);
                      setHighlightedDocumentTypeIndex(-1);
                    } else {
                      setDocumentTypeDropdownOpen(true);
                      setHighlightedDocumentTypeIndex(0);
                    }
                  }

                  if (e.key === "Escape") {
                    setDocumentTypeDropdownOpen(false);
                    setHighlightedDocumentTypeIndex(-1);
                  }

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setDocumentTypeDropdownOpen(true);
                    setHighlightedDocumentTypeIndex((prev) =>
                      prev < DOCUMENT_TYPES.length - 1 ? prev + 1 : 0,
                    );
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setDocumentTypeDropdownOpen(true);
                    setHighlightedDocumentTypeIndex((prev) =>
                      prev > 0 ? prev - 1 : DOCUMENT_TYPES.length - 1,
                    );
                  }
                }}
                className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                    : isBitramp()
                      ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                      : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                }`}
              >
                {formData.document_type || "Select Document Type"}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                >
                  <path
                    d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                    fill={isBitramp() ? "#fff" : "#737791"}
                  />
                </svg>
              </div>

              <AnimatePresence>
                {documentTypeDropdownOpen && !isDisabled && (
                  <motion.div
                    ref={documentTypeRef}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`${
                      isBitramp()
                        ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                        : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                    }`}
                  >
                    {DOCUMENT_TYPES.map((type, index) => (
                      <div
                        key={type}
                        onClick={() => {
                          handleDocumentTypeSelect(type);
                          setDocumentTypeDropdownOpen(false);
                          setHighlightedDocumentTypeIndex(-1);
                        }}
                        onMouseEnter={() =>
                          setHighlightedDocumentTypeIndex(index)
                        }
                        className={`px-4 py-2 cursor-pointer ${
                          isBitramp()
                            ? highlightedDocumentTypeIndex === index
                              ? "bg-[#262626]"
                              : "hover:bg-[#262626]"
                            : highlightedDocumentTypeIndex === index
                              ? "bg-gray-100"
                              : "hover:bg-gray-100"
                        }`}
                      >
                        {type}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Document Number <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="document_number"
              value={formData.document_number}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="Document Number"
              required
              disabled={isDisabled}
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Country <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="relative w-full" ref={containerRefDocumentCountry}>
              <div
                id="document_country"
                tabIndex={0}
                role="button"
                onClick={() =>
                  setDocumentCountryDropdownOpen(!documentCountryDropdownOpen)
                }
                onFocus={() => {
                  if (!isDisabled) {
                    setDocumentCountryDropdownOpen(true);
                    setHighlightedIndexDocumentCountry(
                      filtered.length > 0 ? 0 : -1,
                    );
                  }
                }}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === "Tab") {
                    if (!documentCountryDropdownOpen) {
                      e.preventDefault();
                      setDocumentCountryDropdownOpen(true);
                      setHighlightedIndexDocumentCountry(
                        filtered.length > 0 ? 0 : -1,
                      );
                      return;
                    }
                  }

                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      documentCountryDropdownOpen &&
                      filtered.length > 0 &&
                      highlightedIndexDocumentCountry >= 0
                    ) {
                      handleDocumentCountrySelect(
                        filtered[highlightedIndexDocumentCountry].name,
                      );
                      closeDropdown();
                    } else {
                      setDocumentCountryDropdownOpen(true);
                      setHighlightedIndexDocumentCountry(
                        filtered.length > 0 ? 0 : -1,
                      );
                    }
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    closeDropdown();
                  }

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setDocumentCountryDropdownOpen(true);
                    setHighlightedIndexDocumentCountry((prev) =>
                      prev < filtered.length - 1 ? prev + 1 : 0,
                    );
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setDocumentCountryDropdownOpen(true);
                    setHighlightedIndexDocumentCountry((prev) =>
                      prev > 0 ? prev - 1 : filtered.length - 1,
                    );
                  }
                }}
                className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                    : isBitramp()
                      ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                      : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                }`}
              >
                {formData.document_country || "Select Country"}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                >
                  <path
                    d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                    fill={isBitramp() ? "#fff" : "#737791"}
                  />
                </svg>
              </div>

              <AnimatePresence>
                {documentCountryDropdownOpen && !isDisabled && (
                  <motion.div
                    ref={listRefDocumentCountry}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`${
                      isBitramp()
                        ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                        : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                    }`}
                  >
                    <input
                      type="text"
                      placeholder="Search country..."
                      value={searchTermDocumentCountry}
                      onChange={(e) => {
                        setSearchTermDocumentCountry(e.target.value);
                        setHighlightedIndexDocumentCountry(0);
                      }}
                      onKeyDown={(e) => {
                        if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key))
                          e.preventDefault();
                        if (e.key === "Enter" && filtered.length > 0) {
                          handleDocumentCountrySelect(
                            filtered[
                              highlightedIndexDocumentCountry >= 0
                                ? highlightedIndexDocumentCountry
                                : 0
                            ].name,
                          );
                          closeDropdown();
                        }
                      }}
                      className="w-full px-4 py-2 border-b border-gray-200 outline-none text-sm bg-inherit Input !rounded-r-[0rem] !rounded-bl-[0rem]"
                    />

                    {filtered.length > 0 ? (
                      filtered.map((country, i) => (
                        <div
                          key={country.code}
                          onClick={() => {
                            handleDocumentCountrySelect(country.name);
                            closeDropdown();
                          }}
                          onMouseEnter={() =>
                            setHighlightedIndexDocumentCountry(i)
                          }
                          className={`px-4 py-2 cursor-pointer ${
                            isBitramp()
                              ? highlightedIndexDocumentCountry === i
                                ? "bg-[#262626]"
                                : "hover:bg-[#262626]"
                              : highlightedIndexDocumentCountry === i
                                ? "bg-gray-100"
                                : "hover:bg-gray-100"
                          }`}
                        >
                          {country.name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-400 italic">
                        No results
                      </div>
                    )}

                    <div
                      onClick={() => {
                        handleDocumentCountrySelect("Other");
                        closeDropdown();
                      }}
                      className={`px-4 py-2 cursor-pointer ${
                        isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                      }`}
                    >
                      Other
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <div className="w-2/5">
            <label
              htmlFor="document_expiration"
              className="label_design SmallText light-blue medium  ml-[0.3rem]"
            >
              Expiry Date <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="date"
              id="document_expiration"
              name="document_expiration"
              value={formData.document_expiration ?? ""}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              required
              disabled={isDisabled}
              min={today}
            />
            <style>
              {`
                input[type="date"].input_design::-webkit-calendar-picker-indicator {
                filter: ${isBitramp() ? "invert(1)" : "invert(0)"};
                cursor: pointer;
                }
              `}
            </style>
          </div>
        </div>

        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Address
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-[81.5%]">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Street Address <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="street_address"
              value={formData.street_address}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="Street Address"
              required
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Street Address 2
            </label>
            <input
              type="text"
              name="street_address2"
              value={formData.street_address2}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="Street Address 2"
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Post Code <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="post_code"
              value={formData.post_code}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="Post Code"
              required
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              City <span className="!text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              placeholder="City"
              disabled={isDisabled}
            />
          </div>

          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Country <span className="!text-red-500 ml-1">*</span>
            </label>
            <div className="relative w-full" ref={containerRefCountry}>
              <div
                id="country"
                tabIndex={0}
                role="button"
                onFocus={() => {
                  if (!isDisabled) {
                    setCountryDropdownOpen(true);
                    setHighlightedIndexCountry(filtered.length > 0 ? 0 : -1);
                  }
                }}
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                onKeyDown={(e) => {
                  if (isDisabled) return;

                  if (e.key === "Tab") {
                    if (!countryDropdownOpen) {
                      e.preventDefault();
                      setCountryDropdownOpen(true);
                      setHighlightedIndexCountry(filtered.length > 0 ? 0 : -1);
                      return;
                    }
                  }

                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (
                      countryDropdownOpen &&
                      filtered.length > 0 &&
                      highlightedIndexCountry >= 0
                    ) {
                      handleCountrySelect(
                        filtered[highlightedIndexCountry].name,
                      );
                      closeCountryDropdown();
                    } else {
                      setCountryDropdownOpen(true);
                      setHighlightedIndexCountry(filtered.length > 0 ? 0 : -1);
                    }
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    closeCountryDropdown();
                  }

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setCountryDropdownOpen(true);
                    setHighlightedIndexCountry((prev) =>
                      prev < filtered.length - 1 ? prev + 1 : 0,
                    );
                  }

                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setCountryDropdownOpen(true);
                    setHighlightedIndexCountry((prev) =>
                      prev > 0 ? prev - 1 : filtered.length - 1,
                    );
                  }
                }}
                className={`px-4 py-[0.55rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                  isDisabled
                    ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                    : isBitramp()
                      ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                      : "cursor-pointer bg-[#f8f8f8] border border-[#D4D4D4]"
                }`}
              >
                {formData.country || "Select Country"}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="10"
                  viewBox="0 0 11 10"
                  fill="none"
                >
                  <path
                    d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                    fill={isBitramp() ? "#fff" : "#737791"}
                  />
                </svg>
              </div>

              <AnimatePresence>
                {countryDropdownOpen && !isDisabled && (
                  <motion.div
                    ref={listRefCountry}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`${
                      isBitramp()
                        ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                        : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                    }`}
                  >
                    <input
                      type="text"
                      placeholder="Search country..."
                      value={searchTermCountry}
                      onChange={(e) => {
                        setSearchTermCountry(e.target.value);
                        setHighlightedIndexCountry(0);
                      }}
                      onKeyDown={(e) => {
                        if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key))
                          e.preventDefault();
                        if (e.key === "Enter" && filtered.length > 0) {
                          handleCountrySelect(
                            filtered[
                              highlightedIndexCountry >= 0
                                ? highlightedIndexCountry
                                : 0
                            ].name,
                          );
                          closeCountryDropdown();
                        }
                      }}
                      className="w-full px-4 py-2 border-b border-gray-200 outline-none text-sm bg-inherit Input !rounded-r-[0rem] !rounded-bl-[0rem]"
                    />

                    {filtered.length > 0 ? (
                      filtered.map((country, i) => (
                        <div
                          key={country.code}
                          onClick={() => {
                            handleCountrySelect(country.name);
                            closeCountryDropdown();
                          }}
                          onMouseEnter={() => setHighlightedIndexCountry(i)}
                          className={`px-4 py-2 cursor-pointer ${
                            isBitramp()
                              ? highlightedIndexCountry === i
                                ? "bg-[#262626]"
                                : "hover:bg-[#262626]"
                              : highlightedIndexCountry === i
                                ? "bg-gray-100"
                                : "hover:bg-gray-100"
                          }`}
                        >
                          {country.name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-400 italic">
                        No results
                      </div>
                    )}

                    <div
                      onClick={() => {
                        handleCountrySelect("Other");
                        closeCountryDropdown();
                      }}
                      className={`px-4 py-2 cursor-pointer ${
                        isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                      }`}
                    >
                      Other
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <div className="w-2/5">
            <label
              htmlFor="date_address_became_residential"
              className="label_design SmallText light-blue medium ml-[0.3rem]"
            >
              Date from which the above address became your residential address
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="date"
              id="date_address_became_residential"
              name="date_address_became_residential"
              value={formData.date_address_became_residential ?? ""}
              onChange={handleChange}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              required
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Upload Required Documents
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mb-6 w-full">
          <div className="py-3 flex justify-between items-center w-full px-14 mt-[1rem]">
            <label className="flex medium light-blue">
              Proof of Address <span className="text-red-500 ml-1">*</span>
              <Tooltip
                className={`${
                  isBitramp()
                    ? "bg-[#464646] text-[#ADADAD] w-[30%] pointer-events-none"
                    : "bg-[#F6F6F6] text-[#ADADAD] w-[30%] pointer-events-none"
                } `}
                content="AN ORIGINAL OR CERTIFIED TRUE COPY* OF A UTILITY, RATES OR LOCAL AUTHORITY BILL OF RECENT DATE (WITHIN THREE MONTHS), SHOWING YOUR NAME AND RESIDENTIAL ADDRESS."
                placement="right"
                trigger="hover"
              >
                <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
                  ?
                </div>
              </Tooltip>
            </label>
            <span
              onClick={() =>
                handleOpenDocument(userIndividual?.data?.proof_of_address ?? "")
              }
              className="flex-1 text-end cursor-pointer z-[99] mr-4 dark-gray"
            >
              {getFileName(
                poa ?? userIndividual?.data?.proof_of_address ?? null,
              )}
            </span>
            {(poa || userIndividual?.data?.proof_of_address) && (
              <div className="mr-4"></div>
            )}
            <label
              className={`${
                // userIndividual?.data?.proof_of_address !== null || poa
                //   ? "Outline_button"
                //   : "Outline_button"
                isBitramp()
                  ? userIndividual?.data?.proof_of_address !== null || poa
                    ? "Upload_button_bitramp"
                    : "Upload_button_bitramp"
                  : userIndividual?.data?.proof_of_address !== null || poa
                    ? "Outline_button"
                    : "Outline_button"
              }`}
              tabIndex={0}
              role="button"
            >
              Upload
              <input
                type="file"
                name="proofOfAddress"
                onChange={handlePOA}
                disabled={isDisabled}
                className="hidden"
              />
            </label>
          </div>
          <div className="w-full ml-auto h-[1px] bg-[#0000001a] rounded-lg" />
          <div className="py-3 flex justify-between items-center w-full px-14">
            <label className=" medium light-blue flex">
              Proof of Income <span className="text-red-500 ml-1">*</span>
              <Tooltip
                className={`${
                  isBitramp()
                    ? "bg-[#464646] text-[#ADADAD] w-[30%] pointer-events-none"
                    : "bg-[#F6F6F6] text-[#ADADAD] w-[30%] pointer-events-none"
                } `}
                content="A PROOF OF SOURCE OF INCOME/WEALTH/FUNDS (E.I. TAX DECLARATION, SHARES SALE AGREEMENT, EMPLOYMENT AGREEMENT, DONATION AGREEMENT). THIS SHOULD INCLUDE CONFIRMATION OF YOUR FULL NAME, HOME ADDRESS."
                placement="right"
                trigger="hover"
              >
                <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
                  ?
                </div>
              </Tooltip>
            </label>
            <span
              onClick={() =>
                handleOpenDocument(userIndividual?.data?.proof_of_income ?? "")
              }
              className="flex-1 text-end cursor-pointer z-[99] mr-4 dark-gray"
            >
              {getFileName(
                poi ?? userIndividual?.data?.proof_of_income ?? null,
              )}
            </span>
            {(poi || userIndividual?.data?.proof_of_income) && (
              <div className="mr-4"></div>
            )}
            <label
              className={`cloud__button cursor-pointer ${
                isBitramp()
                  ? userIndividual?.data?.proof_of_income !== null || poi
                    ? "Upload_button_bitramp"
                    : "Upload_button_bitramp"
                  : userIndividual?.data?.proof_of_income !== null || poi
                    ? "Outline_button"
                    : "Outline_button"
                // userIndividual?.data?.proof_of_income !== null || poi
                //   ? "Outline_button"
                //   : "Outline_button"
              }`}
              tabIndex={0}
              role="button"
            >
              Upload
              <input
                type="file"
                name="proofOfIncome"
                onChange={handlePOI}
                disabled={isDisabled}
                className="hidden"
              />
            </label>
          </div>
          <div className="w-full ml-auto h-[1px] bg-[#0000001a] rounded-lg" />
          <div className="py-3 flex justify-between items-center w-full px-14">
            <label className=" medium light-blue flex ">
              Passport / ID <span className="text-red-500 ml-1">*</span>
              <Tooltip
                className={`${
                  isBitramp()
                    ? "bg-[#464646] text-[#ADADAD] w-[30%] pointer-events-none"
                    : "bg-[#F6F6F6] text-[#ADADAD] w-[30%] pointer-events-none"
                } `}
                content="A CERTIFIED TRUE COPY* OF YOUR PASSPORT, INCLUDING ALL PASSPORTS WHERE NATIONALITY IS HELD."
                placement="right"
                trigger="hover"
              >
                <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
                  ?
                </div>
              </Tooltip>
            </label>
            <span
              onClick={() =>
                handleOpenDocument(userIndividual?.data?.passport_file ?? "")
              }
              className="flex-1 text-end cursor-pointer z-[99] mr-4 dark-gray"
            >
              {getFileName(
                passid ?? userIndividual?.data?.passport_file ?? null,
              )}
            </span>
            {(passid || userIndividual?.data?.passport_file) && (
              <div className="mr-4"></div>
            )}
            <label
              className={`cloud__button cursor-pointer ${
                // userIndividual?.data?.passport_file !== null || passid
                //   ? "Outline_button"
                //   : "Outline_button"
                isBitramp()
                  ? userIndividual?.data?.passport_file !== null || passid
                    ? "Upload_button_bitramp"
                    : "Upload_button_bitramp"
                  : userIndividual?.data?.passport_file !== null || passid
                    ? "Outline_button"
                    : "Outline_button"
              }`}
              tabIndex={0}
              role="button"
            >
              Upload
              <input
                type="file"
                name="passportFile"
                onChange={handlePASSID}
                disabled={isDisabled}
                className="hidden"
              />
            </label>
          </div>
          <div className="w-full ml-auto h-[1px] bg-[#0000001a] rounded-lg" />
        </div>
      </form>
      <form
        onSubmit={handleSubmit}
        className="w-full mx-auto p-8 bg-white rounded-b-md Container rounded-t-[0rem] pshadow Text"
      >
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Business Info
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Business Name
            </label>
            <input
              type="text"
              name="business_name"
              value={formDataPlus.business_name}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Tax Number
            </label>
            <input
              type="text"
              name="tax_number"
              value={formDataPlus.tax_number}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-[81.5%]">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Business Address
            </label>
            <input
              type="text"
              name="business_address"
              value={formDataPlus.business_address}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
        </div>

        {/* Telephone Number Section */}
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Telephone Number
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-2/5 flex flex-col justify-between">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Office
            </label>

            <MuiPhoneNumber
              defaultCountry="gi"
              value={formDataPlus.office_phone}
              disabled={isDisabled}
              onChange={(value) =>
                setFormDataPlus({
                  ...formDataPlus,
                  office_phone: typeof value === "string" ? value : "",
                })
              }
              inputClass="input_design Input w-full"
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  ":hover": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  "& input": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                "& .MuiInputAdornment-root": {
                  marginLeft: "0.6rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiPaper-root": {
                  backgroundColor: "#2B2B2B !important",
                  color: "#fff !important",
                },
                ".MuiMenuItem-root": {
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#1a1a1a !important",
                  },
                },
                ".Mui-selected": {
                  backgroundColor: "#202a43 !important",
                },
              }}
            />
          </div>
          <div className="w-2/5 flex flex-col justify-between">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Business
            </label>

            <MuiPhoneNumber
              defaultCountry="gi"
              value={formDataPlus.business_phone}
              disabled={isDisabled}
              onChange={(value) =>
                setFormDataPlus({
                  ...formDataPlus,
                  business_phone: typeof value === "string" ? value : "",
                })
              }
              inputClass="input_design Input w-full"
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  ":hover": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  "& input": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                "& .MuiInputAdornment-root": {
                  marginLeft: "0.6rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiPaper-root": {
                  backgroundColor: "#2B2B2B !important",
                  color: "#fff !important",
                },
                ".MuiMenuItem-root": {
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#1a1a1a !important",
                  },
                },
                ".Mui-selected": {
                  backgroundColor: "#202a43 !important",
                },
              }}
            />
          </div>
        </div>

        {/* Email Section */}
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            E-mail
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Office
            </label>
            <input
              type="email"
              name="office_email"
              value={formDataPlus.office_email}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Business
            </label>
            <input
              type="email"
              name="business_email"
              value={formDataPlus.business_email}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-[81.5%]">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Occupation / Profession
            </label>
            <input
              type="text"
              name="occupation"
              value={formDataPlus.occupation}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
        </div>

        {/* Correspondence and Mailing Address Section */}
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Correspondence and Mailing Address
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Address
            </label>
            <input
              type="text"
              name="mailing_address"
              value={formDataPlus.mailing_address}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Date of birth
            </label>
            <input
              type="date"
              name="date_of_birth"
              value={formDataPlus.date_of_birth ?? ""}
              onChange={handleChangePLUS}
              max={minimumDateToBe18}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Town and Country of Birth
            </label>
            <input
              type="text"
              name="town_and_country_of_birth"
              value={formDataPlus.town_and_country_of_birth}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
          <div className="w-2/5">
            <label className="SmallText light-blue medium ml-[0.3rem]">
              Country of Domicile
            </label>
            <input
              type="text"
              name="domicile_country"
              value={formDataPlus.domicile_country}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="input_design Input"
            />
          </div>
        </div>

        {/* Country or Countries of Tax Residence Section */}
        <div className="w-full flex items-center pt-2 pb-2">
          <h2
            className={`w-fit whitespace-nowrap semi-bold ${
              isBitramp() ? "text-white" : ""
            }`}
          >
            Country or Countries of Tax Residence
          </h2>
          {isBitramp() ? (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-[1rem]">
          <div className="flex flex-wrap w-full">
            <label className="SmallText light-blue medium basis-full ml-[0.3rem]">
              Country 1
            </label>
            <input
              type="text"
              name="tax_residence_country1"
              value={formDataPlus.tax_residence_country1}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="mt-1 block w-2/5 border-gray-300 shadow-sm Input sm:text-sm"
            />
            <input
              type="text"
              name="tax_residence_number1"
              value={formDataPlus.tax_residence_number1}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } mt-1 block w-2/6 border-gray-300 Input sm:text-sm ml-4`}
              placeholder="TIN 1"
            />
          </div>
          <div className="flex flex-wrap w-full">
            <label className="SmallText light-blue medium basis-full ml-[0.3rem]">
              Country 2
            </label>
            <input
              type="text"
              name="tax_residence_country2"
              value={formDataPlus.tax_residence_country2}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="mt-1 block w-2/5 border-gray-300 Input sm:text-sm"
            />
            <input
              type="text"
              name="tax_residence_number2"
              value={formDataPlus.tax_residence_number2}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } mt-1 block w-2/6 border-gray-300 Input sm:text-sm ml-4`}
              placeholder="TIN 2"
            />
          </div>
          <div className="flex flex-wrap w-full">
            <label className="SmallText light-blue medium basis-full ml-[0.3rem]">
              Country 3
            </label>
            <input
              type="text"
              name="tax_residence_country3"
              value={formDataPlus.tax_residence_country3}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className="mt-1 block w-2/5 border-gray-300 Input sm:text-sm"
            />
            <input
              type="text"
              name="tax_residence_number3"
              value={formDataPlus.tax_residence_number3}
              onChange={handleChangePLUS}
              disabled={isDisabled}
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } mt-1 block w-2/6 border-gray-300 Input sm:text-sm ml-4`}
              placeholder="TIN 3"
            />
          </div>
        </div>

        {userIndividual?.data?.status?.status == "declined" ||
        user?.is_staff ? (
          <div>
            <div className="w-full flex items-center pt-2 pb-2">
              <h2
                className={`${
                  isBitramp() ? "text-white" : ""
                } w-fit whitespace-nowrap semi-bold`}
              >
                Reason of decline
              </h2>
              <div
                className={`${
                  isBitramp() ? "bg-[#464646]" : "bg-[#0000001a]"
                } w-full ms-4 ml-auto h-[1px] rounded-lg`}
              />
            </div>
            <div className="w-[81.5%] mb-6 ml-14">
              <textarea
                id="decline_reason"
                name="decline_reason"
                placeholder="Type"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className={`${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } input_design Input`}
                disabled={!canEditNaturalPerson}
              />
            </div>
          </div>
        ) : null}
        <div className="flex gap-4">
          {canEditNaturalPerson ? (
            <>
              <button
                type="button"
                disabled={isDisabled}
                onClick={(e) => handleSubmitWithStatus(e, Status.APPROVED)}
                className="Approve_button"
              >
                {formData?.status?.status === Status.APPROVED
                  ? "Approved"
                  : "Approve"}
                {formData?.status?.status === Status.APPROVED ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </button>
              <button
                type="button"
                disabled={isDisabled}
                onClick={(e) => handleSubmitWithStatus(e, Status.REJECTED)}
                className={`${
                  isBitramp() ? "Decline_button" : "Outline_red_button"
                } `}
              >
                {formData?.status?.status === Status.REJECTED
                  ? "Declined"
                  : "Decline"}
                {formData?.status?.status === Status.REJECTED ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : null}
              </button>
            </>
          ) : (
            <button type="submit" className="Primary_button">
              {addNaturalPerson.isPending ? "Loading..." : "Save"}
            </button>
          )}

          {canEdit ? (
            <Button
              onClick={() => {
                const payload = {
                  phone: formData.phone,
                  title: formData.title,
                  other_title: formData.other_title,
                  first_name: formData.first_name,
                  last_name: formData.last_name,
                  gender: formData.gender,
                  marital_status: formData.marital_status,
                  email: formData.email,
                  document_type: formData.document_type,
                  document_number: formData.document_number,
                  document_country: formData.document_country,
                  document_expiration: formData.document_expiration,
                  street_address: formData.street_address,
                  street_address2: formData.street_address2,
                  post_code: formData.post_code,
                  city: formData.city,
                  country: formData.country,
                  date_address_became_residential:
                    formData.date_address_became_residential,
                };
                const payloadPLUS = {
                  business_name: formDataPlus.business_name,
                  tax_number: formDataPlus.tax_number,
                  business_address: formDataPlus.business_address,
                  office_phone: formDataPlus.office_phone,
                  business_phone: formDataPlus.business_phone,
                  office_email: formDataPlus.office_email,
                  business_email: formDataPlus.business_email,
                  occupation: formDataPlus.occupation,
                  mailing_address: formDataPlus.mailing_address,
                  date_of_birth:
                    formDataPlus.date_of_birth != ""
                      ? formDataPlus.date_of_birth
                      : null,
                  town_and_country_of_birth:
                    formDataPlus.town_and_country_of_birth ?? "",
                  domicile_country: formDataPlus.domicile_country ?? "",
                  tax_residence_country1:
                    formDataPlus.tax_residence_country1 ?? "",
                  tax_residence_number1:
                    formDataPlus.tax_residence_number1 ?? "",
                  tax_residence_country2:
                    formDataPlus.tax_residence_country2 ?? "",
                  tax_residence_number2:
                    formDataPlus.tax_residence_number2 ?? "",
                  tax_residence_country3:
                    formDataPlus.tax_residence_country3 ?? "",
                  tax_residence_number3:
                    formDataPlus.tax_residence_number3 ?? "",
                };
                if (
                  (poa === null || poi === null || passid === null) &&
                  (userIndividual?.data?.proof_of_address === null ||
                    userIndividual?.data?.proof_of_income === null ||
                    userIndividual?.data?.passport_file === null)
                ) {
                  showSnackBar(
                    "Please upload all required documents",
                    "error",
                    {
                      vertical: "top",
                      horizontal: "right",
                    },
                    5000,
                  );
                  return;
                }
                editNaturalPersonMutation.mutate(payload);
                editNaturalPersonPLUS.mutate(payloadPLUS);
              }}
              className="Primary_button"
            >
              Save
            </Button>
          ) : null}

          {error.message ? (
            <div className="text-red-500">{error.message}</div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default NaturalPersonForm;
